import { Box, Chip, CircularProgress, Modal, Typography } from "@mui/material";
import React, { useState } from "react";
import { errorToastMessage } from "../../utils/toast";
import AddTagModal from "../ParticipantRecords/ParticipantTags/AddTagModal";
import AddNoteModal from "../ParticipantRecords/ParticipantNotes/AddNoteModal";
import { ModalBaseStyles, ModalHeader } from "../Common/styles/modal";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import http from "../../utils/http";
import { setParticipantDetails } from "../../Redux/reducers/responseSlice";
import { useParams } from "react-router-dom";
import { permissions } from "../../utils/roles";

const HeaderTags = () => {
  const dispatch = useAppDispatch();
  const tagsList = useAppSelector(
    (state) => state.response.participantDetails.tags
  );

  const sitePermissions = useAppSelector(
    (state) => state.study.sitePermissions
  );
  const dataEditable = useAppSelector((state) => state.study.dataEditable);

  const siteId = useAppSelector((state) => state.response.siteId);
  const [canEdit] = useState(
    sitePermissions?.[siteId]?.permissions?.includes(
      permissions.editParticipant
    ) && dataEditable
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [showMoreModal, setShowMoreModal] = useState(false);
  const [showAddTag, setShowAddTag] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const { id: studyId, participantId } = useParams();

  const showMore = (tagsList?.length ?? 0) > 2;

  const tagKey = (tag: { id: string; studyId?: string | null }) =>
    `${tag.studyId ?? "registry"}-${tag.id}`;

  const handleShowMore = () => {
    setShowMoreModal(true);
  };

  const closeShowMore = () => {
    setShowMoreModal(false);
  };

  const handleAddTag = () => {
    setShowAddTag(true);
  };

  const closeAddTag = () => {
    setShowAddTag(false);
  };

  const refreshTags = async () => {
    try {
      setLoading(true);
      const res = await http.get(
        `/participant-tags?participantId=${participantId}&studyId=${studyId}`
      );
      const tagsList = res?.data?.data?.map((tag: any) => ({
        id: tag?.tag?.id,
        name: tag?.tag?.name,
        studyId: tag?.studyId,
      }));
      dispatch(setParticipantDetails({ key: "tags", value: tagsList }));
    } catch (error) {
      errorToastMessage(error as Error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.5 }}>
          {canEdit && (
            <Typography
              color={"primary"}
              variant="body1"
              fontWeight={"regular"}
              sx={{
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => setShowAddNote(true)}
            >
              + Add Notes
            </Typography>
          )}
          {canEdit && (
            <Typography
              color={"primary"}
              variant="body1"
              fontWeight={"regular"}
              sx={{
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={handleAddTag}
            >
              + Add Tag
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            {tagsList?.slice(0, 2).map((tag) => (
              <Chip
                sx={{
                  backgroundColor: "secondary.main",
                  paddingX: 0.2,
                  paddingY: 0.25,
                  borderRadius: 1.5,
                  fontSize: 10,
                  fontWeight: 600,
                  color: "primary.main",
                }}
                key={tagKey(tag)}
                label={tag.name}
              />
            ))}
          </Box>
          {showMore && (
            <Typography
              color={"primary"}
              variant="body1"
              fontWeight={"regular"}
              sx={{
                cursor: "pointer",
              }}
              onClick={handleShowMore}
            >
              +{tagsList.length - 2} more
            </Typography>
          )}
        </Box>
      ) : (
        <CircularProgress size={16} />
      )}
      {showAddTag && (
        <AddTagModal
          showModal={showAddTag}
          closeModal={closeAddTag}
          refreshPage={refreshTags}
          existingTags={tagsList}
        />
      )}
      {showAddNote && (
        <AddNoteModal
          showModal={showAddNote}
          closeModal={() => setShowAddNote(false)}
          refreshPage={() => setShowAddNote(false)}
        />
      )}
      {showMoreModal && (
        <Modal open={showMoreModal} onClose={closeShowMore}>
          <Box sx={{ ...ModalBaseStyles, minHeight: "10vh" }}>
            <ModalHeader title={"Tags"} onCloseClick={closeShowMore} />
            <Box>
              {tagsList?.map((tag) => (
                <Chip
                  sx={{
                    backgroundColor: "#F6F1F8",
                    paddingX: "10px",
                    paddingY: "2px",
                    borderRadius: "6px",
                    fontSize: 10,
                    fontWeight: 500,
                    color: "primary.main",
                    m: 0.5,
                  }}
                  key={tagKey(tag)}
                  label={tag.name}
                />
              ))}
            </Box>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default HeaderTags;
